import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchSnapshotLetter } from '../services/api';
import SnapshotLetter, { normalizeSnapshotLetter, SnapshotLetterData } from './SnapshotLetter';
import snapshotLetterSample from '../data/snapshotLetterSample';
import './SnapshotLetter.css';

/**
 * The result page: one snapshot letter for the whole journey.
 *
 * The letter comes from `POST /snapshot/` (`{ idToken, child_id, grade }`),
 * which answers with `{ child_name, grade, tests_completed, snapshot: {...} }`.
 * `normalizeSnapshotLetter` maps that payload onto what the page renders, so
 * every field the backend sends is read in one place.
 */

interface TestResultsProps {
  assessmentData?: any;
  onBackToDashboard?: () => void;
  childId?: string;
  grade?: string;
}

/** In development only, fall back to the reference letter so the page can be worked on offline. */
const PREVIEW_FALLBACK = process.env.NODE_ENV !== 'production';

const hasLetterContent = (letter: SnapshotLetterData | null): letter is SnapshotLetterData =>
  !!letter && (letter.noticed.length > 0 || letter.suggestions.length > 0 || letter.opening.length > 0);

const TestResults: React.FC<TestResultsProps> = ({
  assessmentData: propAssessmentData,
  onBackToDashboard: propOnBackToDashboard,
  childId: propChildId,
  grade: propGrade,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Props when rendered inside the app shell; navigation state when linked to directly.
  const assessmentData = propAssessmentData || (location.state as any)?.assessmentData;
  const childId = propChildId || assessmentData?.child_id || assessmentData?.childId;
  const grade = propGrade || assessmentData?.grade;

  const goBack =
    propOnBackToDashboard || (() => navigate('/app', { state: { showLearningJourney: true } }));

  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // Reuse a snapshot already carried in navigation state rather than refetching.
      const cached =
        assessmentData?.snapshotLetter ||
        (assessmentData?.snapshot ? assessmentData : null) ||
        (assessmentData?.response?.snapshot ? assessmentData.response : null) ||
        null;
      if (cached) {
        setReport(cached);
        setLoading(false);
        return;
      }
      if (!childId || !grade) {
        setError('Missing child or grade information.');
        setLoading(false);
        return;
      }
      try {
        const data = await fetchSnapshotLetter(childId, grade);
        if (!cancelled) {
          if (data?.success === false) setError(data?.message || 'The snapshot could not be generated.');
          else setReport(data);
        }
      } catch (err: any) {
        if (!cancelled) {
          const status = err?.response?.status;
          setError(
            status === 404
              ? 'No snapshot yet — this letter is written once the adventures are finished.'
              : err?.response?.data?.message || err.message || 'Failed to load the snapshot letter.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [assessmentData, childId, grade]);

  if (loading) {
    return (
      <div className="sl-page">
        <div className="sl-state">
          <div>
            <div className="sl-quill" />
            <div className="sl-state__title">Writing your letter…</div>
            <p className="sl-state__text">Gathering everything Eko noticed across the adventures.</p>
          </div>
        </div>
      </div>
    );
  }

  const fallbackName = report?.child_name || assessmentData?.user_name;
  const letter = normalizeSnapshotLetter(report, fallbackName);
  const live = hasLetterContent(letter);
  const resolved = live ? letter : PREVIEW_FALLBACK ? normalizeSnapshotLetter(snapshotLetterSample) : null;
  // The sample reads exactly like a real letter, so say plainly when it is the one on screen.
  const isSample = !!resolved && !live;
  // Borrow the sample's child so the answer tables are reachable in the preview too.
  const reviewChildId = childId || (isSample ? snapshotLetterSample.child_id : undefined);
  const reviewGrade = grade || (isSample ? snapshotLetterSample.grade : undefined);

  if (!resolved) {
    return (
      <div className="sl-page">
        <div className="sl-state">
          <div>
            <div className="sl-state__title">{error ? 'Could not load the letter' : 'No letter yet'}</div>
            <p className="sl-state__text">
              {error || 'This letter is written once all the adventures are finished. Check back shortly.'}
            </p>
            <button type="button" className="sl-btn" onClick={goBack}>
              ← Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {isSample && (
        <div className="sl-sample-note">
          Sample letter, shown in development only — the snapshot API returned no letter for this child.{' '}
          {error}
        </div>
      )}
      <SnapshotLetter letter={resolved} onBack={goBack} childId={reviewChildId} grade={reviewGrade} />
    </>
  );
};

export default TestResults;
